import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Grid,
  PasswordInput,
  Select,
  Space,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { ReactNode, useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import { axiosPrivate } from '../../api/axios';
import { cities, provinces } from '../../data/province-city.array';
import { Countries } from '../../enums/countries.enum';
import { OpenComponent } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import useAuth from '../../hooks/useAuth';
import {
  BranchCreateSchema,
  CreateAccountSchema,
} from '../../schemas/create-account.schema';
import { CreateAccountRequest } from '../../type/create-account.types';
import { ErrorResponse } from '../../type/error-response.types';
import { UserManagementFilterForm } from '../../type/filter.types';
import BackButton from '../buttons/back-button.component';
import PhoneCountryCodeIcon from '../icons/phone-country-code-icon.component';
import XCol4 from '../x-components/grid-col/x-col-4.component';

interface Props {
  filterValues?: UserManagementFilterForm;
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}

function AdminCreateUser(props: Props) {
  const { filterValues, setComponentManager, setRefreshTable } = props;
  const [loading, setLoading] = useState(false);
  const { auth } = useAuth();

  const form = useForm({
    initialValues:
      auth?.role === Roles.ADMINISTRATOR
        ? {
            email: '',
            primary: '',
            mobile: '',

            firstName: '',
            middleName: '',
            noMiddleName: false,
            lastName: '',
            username: '',
            password: '',
            confirmPassword: '',
            pin: '',

            city: '',
            line1: '',
            buildingNameAndNumber: '',
            state: '',
            postalCode: '',
            barangay: '',
            countryId: SENTINEL,
            fax: '',
          }
        : {
            firstName: '',
            middleName: '',
            noMiddleName: false,
            lastName: '',
            username: '',
            password: '',
            confirmPassword: '',
            pin: '',
            email: '',
            primary: '',
          },
    schema: joiResolver(
      auth?.role === Roles.ADMINISTRATOR
        ? CreateAccountSchema
        : BranchCreateSchema
    ),
  });

  const handleSubmit = async () => {
    const values = form.values;

    const request: CreateAccountRequest = {
      partnerId: parseInt(filterValues?.partner || ''),
      resellerId: parseInt(filterValues?.reseller || ''),
      merchantId: parseInt(filterValues?.merchant || ''),
      branchId: parseInt(filterValues?.merchantBranch || ''),

      contact: {
        email: values.email,
        phone: {
          primary: values.primary,
          mobile: values.mobile,
        },
      },

      user: {
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        username: values.username,
        password: values.password,
        pin: values.pin,
        address: {
          city: values.city || '',
          line1: values.line1 || '',
          buildingNameAndNumber: values.buildingNameAndNumber,
          state: values.state || '',
          postalCode: values.postalCode || '',
          barangay: values.barangay,
          countryId: values.countryId,
          fax: values.fax,
        },
      },
    };

    if (!request.resellerId) {
      delete request.resellerId;
    }
    if (!request.merchantId) {
      delete request.merchantId;
    }
    if (!request.branchId) {
      delete request.branchId;
    }
    if (!request.partnerId) {
      delete request.partnerId;
    }

    if (
      request.contact.phone.mobile !== undefined &&
      request.contact.phone.mobile.trim().length < 1
    ) {
      delete request.contact.phone.mobile;
    }

    if (
      request.user.address?.fax !== undefined &&
      request.user.address?.fax.trim().length < 1
    ) {
      delete request.user.address?.fax;
    }
    // if (form.values.countryId !== Countries.PHILIPPINES) {
    //   delete request.user.address?.countryId;
    // }
    if (
      request.user.address?.barangay !== undefined &&
      request.user.address?.barangay.trim().length < 1
    ) {
      delete request.user.address?.barangay;
    }
    if (
      request.user.address?.buildingNameAndNumber !== undefined &&
      request.user.address?.buildingNameAndNumber.trim().length < 1
    ) {
      delete request.user.address?.buildingNameAndNumber;
    }

    if (auth?.role !== Roles.ADMINISTRATOR) {
      delete request.user.address;
    }

    try {
      setLoading(true);
      await axiosPrivate.post('/web/accounts/create', request, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${auth?.accessToken}` },
      });

      showNotification({
        id: 'successfully-created-merchant',
        message: 'Successfully created user',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
      setRefreshTable((prev) => !prev);
      setComponentManager(OpenComponent.HOME);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-create-account',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Container fluid>
        <BackButton
          handleClick={() => {
            setComponentManager(OpenComponent.HOME);
          }}
        />
        <Title order={2} my='lg'>
          User Registration
        </Title>
        <Divider my='xl' />
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Text size='xl' weight={700}>
            User Information
          </Text>
          <Space h='xl' />
          <Grid>
            <XCol4>
              <TextInput
                label={labels.firstName}
                placeholder={placeholders.firstName}
                {...form.getInputProps('firstName')}
              />
            </XCol4>
            <XCol4>
              <TextInput
                label={labels.middleName}
                placeholder={placeholders.middleName}
                disabled={form.values.noMiddleName}
                {...form.getInputProps('middleName')}
              />
              <Space my='xs' />
              <Checkbox
                label="I don't have a middle name"
                checked={form.values.noMiddleName}
                onChange={() => {
                  const noMiddleName = !form.values.noMiddleName;
                  form.setFieldValue('noMiddleName', noMiddleName);
                  form.setFieldValue('middleName', noMiddleName ? '.' : '');
                }}
              />
            </XCol4>
            <XCol4>
              <TextInput
                label={labels.lastName}
                placeholder={placeholders.lastName}
                {...form.getInputProps('lastName')}
              />
            </XCol4>
            <XCol4>
              <TextInput
                label={labels.emailAddress}
                placeholder={placeholders.emailAddress}
                {...form.getInputProps('email')}
              />
            </XCol4>
            <XCol4>
              <TextInput
                label={labels.username}
                placeholder={placeholders.username}
                {...form.getInputProps('username')}
              />
            </XCol4>
            <XCol4>
              <PasswordInput
                label={labels.password}
                placeholder={placeholders.password}
                {...form.getInputProps('password')}
              />
            </XCol4>
            <XCol4>
              <PasswordInput
                label={labels.confirmPassword}
                placeholder={placeholders.confirmPassword}
                {...form.getInputProps('confirmPassword')}
              />
            </XCol4>
            <XCol4>
              <PasswordInput
                label={labels.pin}
                placeholder={placeholders.pin}
                {...form.getInputProps('pin')}
                maxLength={4}
              />
            </XCol4>
          </Grid>
          <Divider my='xl' />
          {auth?.role === Roles.ADMINISTRATOR && (
            <>
              <Text size='xl' weight={700}>
                Address
              </Text>
              <Space h='xl' />
              <Grid>
                <Grid.Col span={12}>
                  <TextInput
                    label={labels.bldgNameNo}
                    placeholder={placeholders.bldgNameNo}
                    {...form.getInputProps('buildingNameAndNumber')}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <TextInput
                    label={labels.streetAddress}
                    placeholder={placeholders.streetAddress}
                    {...form.getInputProps('line1')}
                  />
                </Grid.Col>
                <ResponsiveCol66>
                  <Select
                    label={labels.province}
                    placeholder={placeholders.province}
                    data={provinces}
                    value={form.values.state}
                    onChange={(value: string) => {
                      form.setFieldValue('state', value);
                      form.setFieldValue('city', '');
                    }}
                    searchable
                    nothingFound='No options'
                    error={form.errors.state}
                  />
                </ResponsiveCol66>
                <ResponsiveCol66>
                  <Select
                    label={labels.city}
                    placeholder={placeholders.city}
                    data={
                      form.values.state &&
                      form.values.state.length > 0 &&
                      Object.keys(cities).includes(form.values.state)
                        ? cities[form.values.state]
                        : []
                    }
                    disabled={form.values.state === ''}
                    searchable
                    value={form.values.city}
                    onChange={(value: string) => {
                      form.setFieldValue('city', value);
                    }}
                    error={form.errors.city}
                  />
                </ResponsiveCol66>
                <XCol4>
                  <TextInput
                    label={labels.barangay}
                    placeholder={placeholders.barangay}
                    {...form.getInputProps('barangay')}
                  />
                </XCol4>
                <XCol4>
                  <TextInput
                    label={labels.postalCode}
                    placeholder={placeholders.postalCode}
                    {...form.getInputProps('postalCode')}
                  />
                </XCol4>
                <XCol4>
                  <Select
                    label={labels.country}
                    placeholder={placeholders.country}
                    data={[
                      {
                        label: 'Philippines',
                        value: Countries.PHILIPPINES.toString(),
                      },
                    ]}
                    onChange={(value: string) => {
                      form.setFieldValue('countryId', parseInt(value));
                    }}
                    error={form.errors.countryId}
                  />
                </XCol4>
              </Grid>
              <Divider my='xl' />
            </>
          )}

          <Text size='xl' weight={700}>
            Contact Information
          </Text>
          <Space h='xl' />
          <Grid>
            <ResponsiveCol66>
              <TextInput
                label={labels.contactNumber}
                placeholder={placeholders.contactNumber}
                icon={<PhoneCountryCodeIcon />}
                iconWidth={48}
                {...form.getInputProps('primary')}
              />
            </ResponsiveCol66>
            {auth?.role === Roles.ADMINISTRATOR && (
              <>
                <ResponsiveCol66>
                  <TextInput
                    label={labels.mobileNumber}
                    placeholder={placeholders.mobileNumber}
                    icon={<PhoneCountryCodeIcon />}
                    iconWidth={48}
                    {...form.getInputProps('mobile')}
                  />
                </ResponsiveCol66>
                <ResponsiveCol66>
                  <TextInput
                    label={labels.fax}
                    placeholder={placeholders.fax}
                    {...form.getInputProps('fax')}
                  />
                </ResponsiveCol66>
              </>
            )}
          </Grid>
          <Space />
          <Box
            my='md'
            mt={30}
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button type='submit' loading={loading}>
              Submit
            </Button>
          </Box>
        </form>
      </Container>
    </>
  );
}

const SENTINEL = -999;
const labels = {
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  streetAddress: 'Street address',
  bldgNameNo: 'Building name and number',
  city: 'City',
  province: 'Province',
  barangay: 'Barangay',
  postalCode: 'Postal code',
  country: 'Country',
  contactNumber: 'Primary contact number',
  fax: 'FAX',
  mobileNumber: 'Mobile phone number',
  emailAddress: 'Email address',
  username: 'Username',
  password: 'Password',
  confirmPassword: 'Confirm password',
  pin: 'Terminal PIN',
};
const placeholders = {
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  streetAddress: 'Street address',
  bldgNameNo: 'Building name and number',
  city: 'City',
  province: 'Province',
  barangay: 'Barangay',
  postalCode: 'Postal code',
  country: 'Country',
  contactNumber: 'Primary contact number',
  fax: 'FAX',
  mobileNumber: 'Mobile phone number',
  emailAddress: 'Email address',
  username: 'Username',
  password: 'Password',
  confirmPassword: 'Confirm password',
  pin: 'Terminal PIN',
};

const ResponsiveCol66 = ({ children }: { children: ReactNode }) => {
  return <Grid.Col sm={6}>{children}</Grid.Col>;
};

const ResponsiveCol3333 = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col sm={6} lg={3}>
      {children}
    </Grid.Col>
  );
};

export default AdminCreateUser;
